import {
  apiErrorMessage,
  login as apiLogin,
  logout as apiLogout,
  me as apiMe,
  tokens,
} from "service/moneeflo";

/**
 * The signed-in user, as the new backend describes them.
 *
 * Replaces the loose collection of localStorage keys the Xano login used to
 * scatter around (authToken, channel_id, level, is_owner, name, staff_id...).
 */
export type Session = {
  id: string;
  fullName: string;
  email: string;
  role: string | null;
  organization: {
    id: string;
    name: string;
    type: string | null;
    partnerLevel: number | null;
  } | null;
  permissions: string[];
};

/**
 * Legacy mirror. Large parts of the app still read these keys directly
 * (checkLevel, sidebar, ProtectedRoute). They are written from the session so
 * those screens keep working during the migration, and this function is the
 * single place to delete once nothing reads them.
 */
function writeLegacyKeys(session: Session | null): void {
  if (!session) {
    for (const key of [
      "authToken",
      "channel_id",
      "level",
      "is_owner",
      "name",
      "email",
      "staff_id",
      "owner_name",
      "branch",
    ]) {
      localStorage.removeItem(key);
    }
    return;
  }
  // 174 legacy screens open with `if (localStorage.authToken === null) return`
  // and then JSON.parse it. Without this they fetch nothing and render blanks,
  // which is exactly how the order screen came back empty. They pass the value
  // to the new API through the adapters, which ignore it and use their own
  // bearer token; it exists so the guards pass.
  localStorage.setItem("authToken", JSON.stringify(tokens.access ?? ""));
  localStorage.setItem("name", session.fullName);
  localStorage.setItem("email", session.email);
  localStorage.setItem("staff_id", session.id);
  localStorage.setItem("owner_name", session.organization?.name ?? "");
  localStorage.setItem("channel_id", session.organization?.id ?? "");
  // Six screens default a branch picker from this. The Xano login set it and
  // nothing has since, so every one of them opened on "Select...". Someone
  // posted at a branch belongs to that branch; someone at head office has
  // none and picks one.
  if (session.organization?.type === "branch") {
    localStorage.setItem("branch", session.organization.id);
  } else {
    localStorage.removeItem("branch");
  }
  localStorage.setItem(
    "level",
    String(session.organization?.partnerLevel ?? ""),
  );
  localStorage.setItem(
    "is_owner",
    session.role === "admin" || session.role === "partner_owner" ? "true" : "",
  );
}

/** The click-through preview is signed in as this person and never calls core. */
const PREVIEW_SESSION: Session = {
  id: "preview-user",
  fullName: "R. Kapoor",
  email: "r.kapoor@example.com",
  role: "ops_manager",
  organization: { id: "preview-org", name: "MoneeFlo", type: "head_office", partnerLevel: 1 },
  permissions: ["business.create", "business.review", "order.read", "customer.read"],
};

export const sessionStore = {
  hasTokens: (): boolean => Boolean(tokens.access),

  async load(): Promise<Session | null> {
    if (import.meta.env.VITE_BUSINESS_PREVIEW === "1") {
      writeLegacyKeys(PREVIEW_SESSION);
      return PREVIEW_SESSION;
    }
    if (!tokens.access) return null;
    try {
      const session = (await apiMe()) as Session;
      writeLegacyKeys(session);
      return session;
    } catch (error) {
      // Only a refused session is a reason to sign someone out. Clearing on
      // any failure meant an API restart or a dropped connection logged
      // everyone out and lost whatever they were in the middle of.
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (status === 401 || status === 403) {
        tokens.clear();
        writeLegacyKeys(null);
        return null;
      }
      throw error;
    }
  },

  async signIn(email: string, password: string): Promise<Session> {
    const session = (await apiLogin(email, password)) as Session;
    writeLegacyKeys(session);
    return session;
  },

  async signOut(): Promise<void> {
    await apiLogout();
    writeLegacyKeys(null);
  },
};

export { apiErrorMessage };
